import { Studio } from 'Core/Studio'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { ITask } from 'Core/TaskAdapter'
import { AttributeDefinitionType, MCPTool, TaskConfigType, ToolMetaData } from 'Types/Tasks.types'
import { AgenticAnnotationDataType, ContainerType } from 'Types/Tasks.types'
import { ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'
import { createReactElement } from 'shared/utils/react.utils'
import { AgenticAnnotationTaskUI } from './UI/AgenticAnnotationTaskUI'
import { AgenticAnnotationContainerStrategy } from 'Core/StateContainerCore/ContainerStrategies/AgenticAnnotationContainerStrategy'
import { JSONObject } from 'Types/Common.types'
import { testToolCall } from 'Services/tasks.service'

export class AgenticAnnotationTask
    extends CommonTask<ContainerType.AgenticAnnotation, AgenticAnnotationDataType>
    implements ITask<TaskConfigType<ContainerType.AgenticAnnotation, AgenticAnnotationDataType>>
{
    private mcpTools: MCPTool[] = []
    private toolCallsLoadingIds: string[] = [] // To store the id of the tool calls that are currently loading

    constructor(protected studio: Studio) {
        const agenticAnnotationContainerStrategy = new AgenticAnnotationContainerStrategy()
        super(studio, agenticAnnotationContainerStrategy)
        agenticAnnotationContainerStrategy.setContext({
            task: this,
        })
    }

    public initialize(
        config: TaskConfigType<ContainerType.AgenticAnnotation, AgenticAnnotationDataType>
    ) {
        this.initData()

        this.mcpTools = config.assignment.task.mcp_tools

        const promptBlock = this.studio.blockEngine.addBlock('prompt-block', {
            id: 'prompt-block-1',
            name: 'prompt-block-1',
        })

        const blocks: ITaskBlock[] = [promptBlock]

        this.studio.workbench.enqueueUiUpdate(workbenchUi => {
            this.ui = createReactElement(AgenticAnnotationTaskUI, {
                blocks,
                mcpTools: this.mcpTools,
                onSelectTool: this.addNewTool,
                toolDataFn: () =>
                    this.taskState.getDraftOutputContainer()?.data.data.response.tool_meta_data ??
                    [],
                onTestToolCall: this.testToolCall,
                onReorderTools: this.onReorderTools,
                onDeleteTool: this.onDeleteTool,
                onChange: this.onChangeToolData,
                readOnly: () => this.isReadOnly,
                toolCallsLoadingIdsFn: () => this.toolCallsLoadingIds,
            })

            this.addCommonWorkbenchComponents(workbenchUi, config)

            if (config.assignment.task.attributes) {
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.QUALITY_EVALUATION,
                    groupKey: 'quality-evaluation',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.QUALITY_EVALUATION
                    ),
                    config: { useInCollection: true },
                })
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.EXTRA_INFO,
                    groupKey: 'extra-info',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.EXTRA_INFO
                    ),
                    config: { useInCollection: true },
                })

                workbenchUi.rightSidebar.addDivider()
            }
        })
    }

    private initData = () => {
        /*
         * We save the tool data in the format of
         * {
         *   tool_run_info: {
         *     data: {
         *       value: {
         *         input_params: {},
         *         output: {},
         *         name: '',
         *       },
         *       value_type: 'JSON',
         *     },
         *     value_id: string,
         *   },
         * }
         * But we receive it in the format of
         * {
         *   tool_run_info: {
         *     value: {
         *       input_params: {},
         *       output: {},
         *       name: '',
         *     },
         *   },
         *   value_type: 'JSON',
         * }
         *
         * So we need to convert the data to the format we save.
         *
         * Also, we need to add a violation for each of the tool call so the user has to run it before proceeding.
         */

        const draftOutputContainer = this.taskState.getDraftOutputContainer()
        if (!draftOutputContainer) {
            return
        }

        draftOutputContainer.data.data.response.tool_meta_data =
            draftOutputContainer.data.data.response.tool_meta_data.map(tool => ({
                ...tool,
                tool_run_info: {
                    data: {
                        value: tool.tool_run_info.data?.value ??
                            tool.tool_run_info.value ?? {
                                input_params: {},
                                output: {},
                                name: '',
                            },
                        input_params:
                            tool.tool_run_info.data?.value?.input_params ??
                            tool.tool_run_info.value?.input_params ??
                            {},
                        value_type: 'JSON',
                    },
                    value_id: tool.tool_run_info.value_id ?? crypto.randomUUID(),
                },
            }))
        this.taskState.updateDraftOutputContainer(draftOutputContainer)

        if (this.isReadOnly) {
            return
        }

        draftOutputContainer.data.data.response.tool_meta_data.forEach(tool => {
            this.studio.inputManager.addViolation({
                id: tool.tool_run_info.value_id,
                message: `You need to test the tool call before proceeding. ${
                    tool.tool_run_info.data?.value?.name ?? ''
                }`,
                type: 'error',
            })
        })

        this.handleMinimumToolCallsViolation()
    }

    private addNewTool = (toolName: string) => {
        const draftOutputContainer = this.taskState.getDraftOutputContainer()
        const mcpTool = this.mcpTools.find(mcpTool => mcpTool.name === toolName)
        if (mcpTool && draftOutputContainer) {
            const toolId = crypto.randomUUID()
            draftOutputContainer.data.data.response.tool_meta_data.push({
                tool_run_info: {
                    data: {
                        value: {
                            input_params: {},
                            output: {},
                            name: toolName,
                        },
                        value_type: 'JSON',
                    },
                    value_id: toolId,
                },
            })

            this.studio.inputManager.addViolation({
                id: toolId,
                message: `You need to test the tool call before proceeding. ${toolName}`,
                type: 'error',
            })
            this.autoSaveTask()
            this.handleMinimumToolCallsViolation()
        }
    }

    private testToolCall = async (toolId: string) => {
        const draftOutputContainer = this.taskState.getDraftOutputContainer()
        if (draftOutputContainer) {
            const toolData = draftOutputContainer.data.data.response.tool_meta_data.find(
                tool => tool.tool_run_info.value_id === toolId
            )

            if (!toolData) {
                return
            }

            toolData.tool_run_info.data!.value.output = {}
            this.toolCallsLoadingIds.push(toolId)
            this.ui?.updater.forceUpdate()

            const mcpTool = this.mcpTools.find(
                mcpTool => mcpTool.name === toolData.tool_run_info.data?.value?.name
            )

            const payload = {
                jsonrpc: '2.0',
                id: this.initialConfig?.assignment.task.id ?? '',
                method: 'tools/call',
                params: {
                    name: mcpTool?.name ?? '',
                    arguments: toolData.tool_run_info.data?.value?.input_params ?? {},
                },
            }

            try {
                const resp = await testToolCall(
                    this.initialConfig?.assignment.task.job_id ?? '',
                    payload
                )

                toolData.tool_run_info.data!.value.output = JSON.parse(
                    resp.tool_response.result.content[0].text
                )

                this.studio.inputManager.removeViolation(toolId)

                this.autoSaveTask()
            } catch (error) {
                this.showNotification((error as Error).message, 'error')
            } finally {
                this.toolCallsLoadingIds = this.toolCallsLoadingIds.filter(id => id !== toolId)
                this.ui?.updater.forceUpdate()
            }
        }
    }

    private onChangeToolData = (toolId: string, value: JSONObject) => {
        const draftOutputContainer = this.taskState.getDraftOutputContainer()
        if (draftOutputContainer) {
            const toolData = draftOutputContainer.data.data.response.tool_meta_data.find(
                tool => tool.tool_run_info.value_id === toolId
            )

            if (toolData) {
                toolData.tool_run_info.data!.value.input_params = value
                this.studio.inputManager.addViolation({
                    id: toolId,
                    message: `You need to test the tool call before proceeding. ${
                        toolData.tool_run_info.data?.value?.name ?? ''
                    }`,
                    type: 'error',
                })
            }
        }

        this.autoSaveTask()
        this.ui?.updater.forceUpdate()
    }

    private onReorderTools = (items: ToolMetaData[]) => {
        const draftOutputContainer = this.taskState.getDraftOutputContainer()
        if (draftOutputContainer) {
            draftOutputContainer.data.data.response.tool_meta_data = items
        }
        this.autoSaveTask()
        this.ui?.updater.forceUpdate()
    }

    private onDeleteTool = (toolId: string) => {
        const draftOutputContainer = this.taskState.getDraftOutputContainer()
        if (draftOutputContainer) {
            draftOutputContainer.data.data.response.tool_meta_data =
                draftOutputContainer.data.data.response.tool_meta_data.filter(
                    tool => tool.tool_run_info.value_id !== toolId
                )
            this.studio.inputManager.removeViolation(toolId)
        }
        this.autoSaveTask()
        this.handleMinimumToolCallsViolation()
    }

    private handleMinimumToolCallsViolation = () => {
        const draftOutputContainer = this.taskState.getDraftOutputContainer()

        if (draftOutputContainer?.data.data.response.tool_meta_data.length === 0) {
            this.studio.inputManager.addViolation({
                id: 'no-tool-calls',
                message: 'You need to add at least one tool call before proceeding.',
                type: 'error',
            })
        } else {
            this.studio.inputManager.removeViolation('no-tool-calls')
        }

        this.ui?.updater.forceUpdate()
    }
}
